import { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { X, ImageIcon } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/hooks/use-alert';

interface ProjectImageUploaderProps {
  projectId?: string;
  imageUrl?: string | null;
  onImageUpdate: (imageUrl: string | null) => void;
  className?: string;
  hideLabel?: boolean;
  imageHeight?: string;
  onPendingFileChange?: (file: File | null) => void;
}

export interface ProjectImageUploaderRef {
  uploadPendingFile: (projectId: string) => Promise<string | null>;
  hasPendingFile: () => boolean;
}

export const ProjectImageUploader = forwardRef<ProjectImageUploaderRef, ProjectImageUploaderProps>(({ 
  projectId, 
  imageUrl, 
  onImageUpdate,
  className = "",
  hideLabel = false,
  imageHeight = "h-32",
  onPendingFileChange
}, ref) => {
  const { t } = useTranslation();
  const { alert } = useAlert();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(imageUrl || null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    uploadPendingFile: async (newProjectId: string): Promise<string | null> => {
      if (!pendingFile) return null;
      
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', pendingFile);

        const response = await apiRequest(`/api/projects/${newProjectId}/image`, {
          method: 'POST',
          body: formData,
        });

        const json = await response.json();
        const publicUrl = json?.data?.bildUrl ?? json?.data?.bild_url ?? null;
        if (!publicUrl) {
          throw new Error('Upload did not return image URL');
        }

        setPendingFile(null);
        setPreviewUrl(publicUrl);
        return publicUrl;
      } catch (error) {
        console.error('Upload pending file error:', error);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    hasPendingFile: () => !!pendingFile
  }));

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await processFile(file);
    event.target.value = '';
  };

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      await alert(t('components.projectImageUploader:onlyImagesAllowed'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      await alert(t('components.projectImageUploader:fileTooLarge'));
      return;
    }

    if (!projectId) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setPendingFile(file);
      onPendingFileChange?.(file);
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    if (!projectId) {
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiRequest(`/api/projects/${projectId}/image`, {
        method: 'POST',
        body: formData,
      });

      const json = await response.json();
      const publicUrl = json?.data?.bildUrl ?? json?.data?.bild_url ?? null;
      if (!publicUrl) {
        throw new Error('Upload did not return image URL');
      }

      setPreviewUrl(publicUrl);
      onImageUpdate(publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      await alert(t('components.projectImageUploader:uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      if (pendingFile) {
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        setPendingFile(null);
        setPreviewUrl(null);
        onPendingFileChange?.(null);
        onImageUpdate(null);
        return;
      }

      if (projectId) {
        const response = await apiRequest(`/api/projects/${projectId}`, {
          method: 'PUT',
          body: JSON.stringify({
            bildUrl: null
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to remove image: ${response.status}`);
        }
      }

      setPreviewUrl(null);
      onImageUpdate(null);
    } catch (error) {
      console.error('Remove image error:', error);
      await alert(t('components.projectImageUploader:removeFailed'));
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {!hideLabel && (
        <div className="text-xs font-medium text-slate-600">{t('components.projectImageUploader:projectImage')}</div>
      )}
      
      <div 
        className={`${imageHeight} ${previewUrl ? 'border border-slate-200' : 'border-2 border-dashed border-slate-300'} rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 transition-colors overflow-hidden`}
        onClick={triggerFileSelect}
      >
        {previewUrl ? (
          <div className="relative w-full h-full">
            <img 
              src={previewUrl} 
              alt={t('components.projectImageUploader:projectImage')} 
              className="w-full h-full object-cover"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <>
            <ImageIcon className="h-6 w-6 text-slate-400 mb-1" />
            <p className="text-xs text-slate-500">{t('components.projectImageUploader:clickToUpload')}</p>
          </>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
});

ProjectImageUploader.displayName = 'ProjectImageUploader';
